import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Layers3, FolderInput, MessageSquare, AlertTriangle, ArrowUpRight, Lightbulb, ArrowRight, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CATS = ["Process", "Policy", "Decision Rule", "People/Role", "FAQ"];

export default function Overview() {
  const { workspace, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [personalNudges, setPersonalNudges] = useState([]);
  const [teamNudges, setTeamNudges] = useState([]);
  const [actionRate, setActionRate] = useState(null);

  useEffect(() => {
    api.get("/knowledge/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bot-access/analytics/mine", { params: { days: 7 } })
       .then((r) => setPersonalNudges(r.data?.nudges || []))
       .catch(() => {});
    if (isAdmin) {
      api.get("/bot-access/analytics/workspace", { params: { days: 7 } })
         .then((r) => setTeamNudges(r.data?.nudges || []))
         .catch(() => {});
      api.get("/bot-access/nudges/action-rate", { params: { days: 7 } })
         .then((r) => setActionRate(r.data))
         .catch(() => {});
    }
  }, [isAdmin]);

  const digestNudges = useMemo(() => ([
    ...personalNudges.map((n) => ({ ...n, scope: "personal" })),
    ...teamNudges.map((n) => ({ ...n, scope: "team" })),
  ]), [personalNudges, teamNudges]);

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-10">
        <p className="eyebrow mb-3">Overview</p>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
          {workspace?.name} — knowledge at a glance
        </h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          A live map of your organization's processes, policies, and know-how.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Total entries" value={stats?.total ?? 0} icon={Layers3} to="/app/knowledge" tid="stat-total" />
        <StatCard label="Sources" value={stats?.sources ?? 0} icon={FolderInput} to="/app/sources" tid="stat-sources" />
        <StatCard label="Outdated" value={stats?.outdated ?? 0} icon={AlertTriangle} to="/app/updates" tid="stat-outdated" />
      </div>

      {(digestNudges.length > 0 || (isAdmin && actionRate)) && (
        <section className="mb-10" data-testid="dashboard-nudge-digest">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <h2 className="font-display text-xl">This week's nudges</h2>
            {isAdmin && actionRate && (
              <span className="ml-auto flex items-center gap-1.5 text-xs text-fuchsia-500"
                    data-testid="dashboard-action-rate">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="font-mono font-medium">{actionRate.rate}%</span>
                <span className="text-muted-foreground">of nudges actioned · last {actionRate.days}d</span>
                {actionRate.history && actionRate.history.length > 1 && (
                  <svg width="60" height="20" className="ml-1" data-testid="dashboard-action-rate-sparkline">
                    {(() => {
                      const pts = actionRate.history;
                      const max = Math.max(1, ...pts.map(p => p.count));
                      const trend = pts[pts.length - 1].count - pts[0].count;
                      const color = trend >= 0 ? "#10b981" : "#ef4444";
                      const d = pts.map((p, i) => {
                        const x = (i / (pts.length - 1)) * 58 + 1;
                        const y = 18 - (p.count / max) * 16;
                        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
                      }).join(" ");
                      return <path d={d} fill="none" stroke={color} strokeWidth="1.5" />;
                    })()}
                  </svg>
                )}
              </span>
            )}
          </div>
          {digestNudges.length === 0 ? (
            <div className="card-flat p-5">
              <p className="text-sm text-muted-foreground">All clear — no unaddressed nudges this week.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {digestNudges.slice(0, 6).map((n) => (
                <Link
                  key={`${n.scope}:${n.id}`}
                  to={`${n.cta_href || "/app"}?nudge=${encodeURIComponent(n.id)}&scope=${n.scope}`}
                  data-testid={`dashboard-nudge-${n.id}`}
                  className={`card-flat p-4 hover-lift block ${n.tone === "warn" ? "border-l-4 border-l-amber-500" : "border-l-4 border-l-sky-500"}`}
                >
                  <div className="flex items-start gap-2 mb-1">
                    <Lightbulb className={`w-3.5 h-3.5 mt-0.5 ${n.tone === "warn" ? "text-amber-500" : "text-sky-500"}`} />
                    <p className="text-sm font-medium flex-1">{n.title}</p>
                    {n.scope === "team" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-500">team</span>}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                  <p className="text-xs mt-2 text-sky-500 font-medium">
                    {n.cta_label || "Open"} <ArrowRight className="w-3 h-3 inline" />
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="mb-10">
        <h2 className="font-display text-xl mb-4">Categories</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {CATS.map((c) => (
            <Link key={c} to={`/app/knowledge?category=${encodeURIComponent(c)}`}
              className="card-flat p-5 hover-lift" data-testid={`cat-card-${c}`}>
              <p className="eyebrow mb-3">{c}</p>
              <p className="font-display text-3xl">{stats?.by_category?.[c] ?? 0}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">Departments</h2>
          <Link to="/app/skills" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            View skills files <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="card-flat p-6">
          {stats && Object.keys(stats.by_department || {}).length ? (
            <ul className="space-y-3">
              {Object.entries(stats.by_department).map(([d, n]) => (
                <li key={d} className="flex items-center justify-between text-sm">
                  <span>{d}</span>
                  <span className="font-mono text-muted-foreground">{n} entries</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No entries yet. Upload a source or add manual knowledge to get started.</p>
          )}
        </div>
      </section>

      <section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickAction to="/app/sources" icon={FolderInput} title="Ingest knowledge" desc="Upload PDFs, docs, or add manual entries." tid="quick-ingest" />
          <QuickAction to="/app/chat" icon={MessageSquare} title="Ask the brain" desc="Get sourced, structured answers instantly." tid="quick-ask" />
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, to, tid }) {
  return (
    <Link to={to} className="card-flat p-6 hover-lift block" data-testid={tid}>
      <div className="flex items-start justify-between mb-6">
        <p className="eyebrow">{label}</p>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <p className="font-display text-4xl tracking-tight">{value}</p>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, title, desc, tid }) {
  return (
    <Link to={to} className="card-flat p-6 hover-lift block" data-testid={tid}>
      <Icon className="w-5 h-5 mb-4" />
      <h3 className="font-display text-lg mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </Link>
  );
}
